// Finance Overview — every number here is traceable to a run.
//
// Revenue is claim_lines (home health, built into billing runs or recorded as
// filed outside CAERA) plus claimed DME orders, through lib/receivables.js —
// the same mapping the Claims › Receivables tab uses. Cost is payroll_run_lines,
// which are already per caregiver × client, so revenue and cost meet by
// client without a third calculation in between.
//
// "Revenue & Payroll" used to be a separate page that rebuilt both figures
// from raw AxisCare visits with its own hours rule, hourly-only pay and payer
// labels from before the service model. It could not agree with the runs, so
// it was retired (2026-09-25) and its breakdowns live here, computed from the
// line tables instead.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { format, startOfMonth, endOfMonth, subMonths, startOfQuarter, endOfQuarter, startOfYear } from 'date-fns';
import { StatTile } from '../../components/StatTile';
import DataTable from '../../components/DataTable';
import { lineToReceivable, dmeToReceivable, isOutstanding, isUnfiled, ageReceivables, DME_CLAIMED_STATUSES } from '../../lib/receivables';
import { billRatesFromCatalog, authorizedRevenue, authorizedWeeklyHours, weeksBetween } from '../../lib/authorizedRevenue';
import { RECEIVING_SERVICE } from '../../lib/serviceModel';

const fmt$ = (n) => '$' + Math.round(n || 0).toLocaleString('en-US');
const fmt$2 = (n) => '$' + (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtPct = (n) => isFinite(n) && n != null ? n.toFixed(1) + '%' : '—';
const fmtH = (n) => `${Number(n || 0).toFixed(1)}h`;
const day = (d) => format(d, 'yyyy-MM-dd');

const PERIODS = [
  { key: 'this_month',   label: 'This Month',   range: (now) => [startOfMonth(now), endOfMonth(now)] },
  { key: 'last_month',   label: 'Last Month',   range: (now) => [startOfMonth(subMonths(now, 1)), endOfMonth(subMonths(now, 1))] },
  { key: 'this_quarter', label: 'This Quarter', range: (now) => [startOfQuarter(now), endOfQuarter(now)] },
  { key: 'ytd',          label: 'Year to Date', range: (now) => [startOfYear(now), now] },
];

export default function FinanceOverview() {
  const navigate = useNavigate();
  const [claims, setClaims]       = useState([]);
  const [payLines, setPayLines]   = useState([]);   // payroll_run_lines with their run's week_start and status
  const [runs, setRuns]           = useState([]);   // payroll_runs, newest first
  const [authClients, setAuthClients] = useState([]); // clients in service, with their prior-auth hours
  const [rates, setRates]         = useState(billRatesFromCatalog([]));
  const [period, setPeriod]       = useState('this_month');
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    const load = async () => {
      const [runsRes, linesRes, billingRunsRes, dmeRes, payRes, clientsRes, catalogRes] = await Promise.all([
        supabase.from('payroll_runs').select('id, week_start, week_end, status, total_pay, total_hours, staff_count').order('week_start', { ascending: false }),
        supabase.from('claim_lines').select('id, billing_run_id, client_id, client_name, service_code, service_label, accrued_amount, charge_amount, paid_amount, hours, status, source, payer, service_date, submitted_at, paid_date'),
        supabase.from('billing_runs').select('id, payer, submitted_at'),
        supabase.from('dme_orders').select('id, client_id, client_name, hcpcs_code, claim_amount, expected_amount, paid_amount, claim_submitted_date, claim_paid_date, pod_delivered_date, delivered_date, status').in('status', DME_CLAIMED_STATUSES),
        supabase.from('payroll_run_lines').select('run_id, staff_id, caregiver_name, caregiver_role, client_uuid, client_ref, client_name, hours_adjusted, pay_total, agency_name, run:payroll_runs(week_start, status)'),
        supabase.from('clients').select('id, client_id, first_name, last_name, pa_skilled_hours_week, pa_pss_hours_week').eq('current_status', RECEIVING_SERVICE),
        supabase.from('service_catalog').select('code, skill_kind, default_charge_rate').eq('active', true).eq('billable', true),
      ]);
      const runById = Object.fromEntries((billingRunsRes.data || []).map(r => [r.id, r]));
      setClaims([
        ...(linesRes.data || []).map(l => ({ ...lineToReceivable(l, runById[l.billing_run_id]), service_label: l.service_label || l.service_code, hours: l.hours != null ? Number(l.hours) : null })),
        ...(dmeRes.data || []).map(dmeToReceivable),
      ]);
      setRuns(runsRes.data || []);
      setPayLines((payRes.data || []).map(l => ({ ...l, week_start: l.run?.week_start, run_status: l.run?.status })));
      setAuthClients(clientsRes.data || []);
      setRates(billRatesFromCatalog(catalogRes.data || []));
      setLoading(false);
    };
    load();
  }, []);

  const now = new Date();
  const [pStart, pEnd] = (PERIODS.find(p => p.key === period) || PERIODS[0]).range(now);
  const inPeriod = (d) => d && d >= day(pStart) && d <= day(pEnd);

  // ── Revenue for the period: by service date. Collected: by paid date. ────
  const periodRevenue = useMemo(() => claims.filter(c => c.status !== 'voided' && inPeriod(c.service_date)), [claims, period]); // eslint-disable-line react-hooks/exhaustive-deps
  const periodCollected = useMemo(() => claims.filter(c => c.status === 'paid' && inPeriod(c.paid_date)), [claims, period]); // eslint-disable-line react-hooks/exhaustive-deps
  const periodPay = useMemo(() => payLines.filter(l => inPeriod(l.week_start)), [payLines, period]); // eslint-disable-line react-hooks/exhaustive-deps

  const accrued   = periodRevenue.reduce((s, c) => s + (c.amount || 0), 0);
  const collected = periodCollected.reduce((s, c) => s + (c.paid_amount ?? c.amount ?? 0), 0);
  const payroll   = periodPay.reduce((s, l) => s + (Number(l.pay_total) || 0), 0);
  const hoursBilled = periodRevenue.reduce((s, c) => s + (c.hours || 0), 0);
  const hoursPaid   = periodPay.reduce((s, l) => s + (Number(l.hours_adjusted) || 0), 0);
  const margin = accrued > 0 ? ((accrued - payroll) / accrued) * 100 : null;

  // ── Authorized vs delivered ──────────────────────────────────────────────
  // What the prior auths of everyone in service are worth over the period if
  // every hour is delivered, against what was billed. Same arithmetic as
  // Projections, without the sliders — see lib/authorizedRevenue.js.
  // Only the part of the period that has happened: for "this month" the
  // authorization is worth 4.3 weeks but the claim lines only run to today,
  // and comparing the two used to read as a third of the hours delivered.
  const elapsedEnd = pEnd > now ? now : pEnd;
  const weeksInPeriod = weeksBetween(day(pStart), day(elapsedEnd));
  const authorized = authClients.reduce((s, c) => s + authorizedRevenue(c, rates, weeksInPeriod), 0);
  const authorizedHours = authClients.reduce((s, c) => { const h = authorizedWeeklyHours(c); return s + (h.skilled + h.unskilled) * weeksInPeriod; }, 0);
  const utilisation = authorized > 0 ? (accrued / authorized) * 100 : null;
  const authById = Object.fromEntries(authClients.map(c => [c.id, c]));

  // ── Claims, in one line. The full picture is Claims › Receivables. ───────
  const outstanding = claims.filter(isOutstanding);
  const unfiled     = claims.filter(isUnfiled);
  const aging       = ageReceivables(claims, now);

  // ── Payroll snapshot ─────────────────────────────────────────────────────
  const lastFinalized = runs.find(r => r.status === 'finalized');
  const drafts = runs.filter(r => r.status === 'draft');
  const payrollYTD = runs.filter(r => r.status === 'finalized' && r.week_start >= `${now.getFullYear()}-01-01`)
    .reduce((s, r) => s + (Number(r.total_pay) || 0), 0);

  // ── Breakdowns: revenue and cost meet by client ──────────────────────────
  const byClient = useMemo(() => {
    const map = {};
    const keyOf = (id, name) => id || `name:${(name || '').trim().toLowerCase()}`;
    for (const c of periodRevenue) {
      const k = keyOf(c.client_id, c.client_name);
      (map[k] ||= { key: k, name: c.client_name, revenue: 0, hoursBilled: 0, cost: 0, hoursPaid: 0, payer: c.payer, sources: new Set() });
      map[k].revenue += c.amount || 0; map[k].hoursBilled += c.hours || 0; map[k].sources.add(c._source);
    }
    // Everyone in service appears, billed or not — a client with authorized
    // hours and no revenue is the row worth noticing.
    for (const c of authClients) {
      const h = authorizedWeeklyHours(c);
      if (!h.skilled && !h.unskilled) continue;
      const k = keyOf(c.id, `${c.first_name} ${c.last_name}`);
      (map[k] ||= { key: k, name: `${c.first_name} ${c.last_name}`.trim(), revenue: 0, hoursBilled: 0, cost: 0, hoursPaid: 0, payer: null, sources: new Set() });
    }
    for (const l of periodPay) {
      if (!l.client_uuid && !l.client_name) continue;   // salaried office staff: no client
      const k = keyOf(l.client_uuid, l.client_name);
      (map[k] ||= { key: k, name: l.client_name, revenue: 0, hoursBilled: 0, cost: 0, hoursPaid: 0, payer: null, sources: new Set() });
      map[k].cost += Number(l.pay_total) || 0; map[k].hoursPaid += Number(l.hours_adjusted) || 0;
    }
    return Object.values(map).map(r => {
      const auth = authById[r.key];
      const h = auth ? authorizedWeeklyHours(auth) : null;
      const authHrsWk = h ? h.skilled + h.unskilled : null;
      return {
        ...r, margin: r.revenue - r.cost, marginPct: r.revenue > 0 ? ((r.revenue - r.cost) / r.revenue) * 100 : null,
        authHrsWk, billedHrsWk: r.hoursBilled / weeksInPeriod,
        utilisation: authHrsWk ? (r.hoursBilled / (authHrsWk * weeksInPeriod)) * 100 : null,
      };
    }).sort((a, b) => b.revenue - a.revenue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodRevenue, periodPay, authClients, weeksInPeriod]);

  const byService = useMemo(() => {
    const map = {};
    for (const c of periodRevenue) {
      const k = c.service_label || c.service_code || 'Unknown';
      (map[k] ||= { key: k, revenue: 0, hours: 0, lines: 0 });
      map[k].revenue += c.amount || 0; map[k].hours += c.hours || 0; map[k].lines++;
    }
    return Object.values(map).sort((a, b) => b.revenue - a.revenue);
  }, [periodRevenue]);

  const byPayer = useMemo(() => {
    const map = {};
    for (const c of periodRevenue) {
      const k = c.payer || 'Unknown';
      (map[k] ||= { key: k, revenue: 0, hours: 0, lines: 0 });
      map[k].revenue += c.amount || 0; map[k].hours += c.hours || 0; map[k].lines++;
    }
    return Object.values(map).sort((a, b) => b.revenue - a.revenue);
  }, [periodRevenue]);

  const byCaregiver = useMemo(() => {
    const map = {};
    for (const l of periodPay) {
      const k = l.staff_id || l.caregiver_name;
      (map[k] ||= { key: k, name: l.caregiver_name, role: l.caregiver_role, agency: l.agency_name, hours: 0, pay: 0 });
      map[k].hours += Number(l.hours_adjusted) || 0; map[k].pay += Number(l.pay_total) || 0;
    }
    return Object.values(map).sort((a, b) => b.pay - a.pay);
  }, [periodPay]);

  const NavCard = ({ title, sub, icon, to }) => (
    <div onClick={() => navigate(to)} style={{ background: 'var(--bg)', border: '0.5px solid var(--border)', borderRadius: 10, padding: '14px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}
      onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent2)'}
      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
      <div style={{ width: 34, height: 34, borderRadius: 8, background: 'var(--accent-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17 }}>{icon}</div>
      <div>
        <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>{title}</div>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{sub}</div>
      </div>
      <span style={{ marginLeft: 'auto', color: 'var(--text3)', fontSize: 13 }}>→</span>
    </div>
  );
  const Section = ({ children }) => <div style={{ margin: '4px 0 6px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text3)' }}>{children}</div>;
  const KPI = ({ label, value, sub, warn }) => <StatTile label={label} value={value} sub={sub} tone={warn ? 'alert' : 'neutral'} />;
  const pill = (active) => ({ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', border: '0.5px solid', background: active ? 'var(--accent)' : 'transparent', color: active ? '#fff' : 'var(--text3)', borderColor: active ? 'var(--accent)' : 'var(--border)' });
  const periodLabel = PERIODS.find(p => p.key === period)?.label;

  const clientColumns = [
    { id: 'name', accessorKey: 'name', header: 'Client', size: 180, meta: { mobile: 'primary' },
      cell: info => <div><div style={{ fontWeight: 500 }}>{info.getValue() || '—'}</div>{info.row.original.payer && <div style={{ fontSize: 10, color: 'var(--text3)' }}>{info.row.original.payer}</div>}</div> },
    { id: 'authHrsWk', accessorKey: 'authHrsWk', header: 'Auth hrs/wk', size: 95, meta: { align: 'right', mobile: 'hidden' }, cell: info => info.getValue() != null ? fmtH(info.getValue()) : '—' },
    { id: 'billedHrsWk', accessorKey: 'billedHrsWk', header: 'Billed hrs/wk', size: 110, meta: { align: 'right', mobile: 'hidden' },
      cell: info => { const r = info.row.original; const c = r.utilisation == null ? undefined : r.utilisation < 60 ? 'var(--danger)' : r.utilisation < 85 ? 'var(--warn)' : 'var(--success)';
        return <span style={{ color: c }}>{fmtH(r.billedHrsWk)}{r.utilisation != null && <span style={{ fontSize: 10, marginLeft: 4 }}>({r.utilisation.toFixed(0)}%)</span>}</span>; } },
    { id: 'revenue', accessorKey: 'revenue', header: 'Revenue', size: 110, meta: { align: 'right', mobile: 'primary' }, cell: info => fmt$2(info.getValue()) },
    { id: 'hoursPaid', accessorKey: 'hoursPaid', header: 'Hrs paid', size: 90, meta: { align: 'right', mobile: 'hidden' }, cell: info => fmtH(info.getValue()) },
    { id: 'cost', accessorKey: 'cost', header: 'Payroll', size: 110, meta: { align: 'right', mobile: 'secondary' }, cell: info => fmt$2(info.getValue()) },
    { id: 'margin', accessorKey: 'margin', header: 'Margin', size: 130, meta: { align: 'right', mobile: 'secondary' },
      cell: info => { const r = info.row.original; const c = r.marginPct == null ? 'var(--text3)' : r.marginPct >= 30 ? 'var(--success)' : r.marginPct >= 0 ? 'var(--warn)' : 'var(--danger)';
        return <span style={{ color: c, fontWeight: 500 }}>{fmt$2(r.margin)}{r.marginPct != null && <span style={{ fontSize: 10, marginLeft: 4 }}>({r.marginPct.toFixed(0)}%)</span>}</span>; } },
  ];
  const caregiverColumns = [
    { id: 'name', accessorKey: 'name', header: 'Caregiver', size: 180, meta: { mobile: 'primary' },
      cell: info => <div><div style={{ fontWeight: 500 }}>{info.getValue()}</div><div style={{ fontSize: 10, color: 'var(--text3)' }}>{[info.row.original.role, info.row.original.agency].filter(Boolean).join(' · ')}</div></div> },
    { id: 'hours', accessorKey: 'hours', header: 'Hours', size: 90, meta: { align: 'right', mobile: 'secondary' }, cell: info => fmtH(info.getValue()) },
    { id: 'pay', accessorKey: 'pay', header: 'Pay', size: 110, meta: { align: 'right', mobile: 'primary' }, cell: info => fmt$2(info.getValue()) },
  ];
  const mixColumns = (label) => [
    { id: 'key', accessorKey: 'key', header: label, size: 200, meta: { mobile: 'primary' }, cell: info => <span style={{ fontWeight: 500 }}>{info.getValue()}</span> },
    { id: 'lines', accessorKey: 'lines', header: 'Lines', size: 70, meta: { align: 'right', mobile: 'hidden' } },
    { id: 'hours', accessorKey: 'hours', header: 'Hours', size: 80, meta: { align: 'right', mobile: 'secondary' }, cell: info => fmtH(info.getValue()) },
    { id: 'revenue', accessorKey: 'revenue', header: 'Revenue', size: 110, meta: { align: 'right', mobile: 'primary' },
      cell: info => <span>{fmt$2(info.getValue())}<span style={{ fontSize: 10, color: 'var(--text3)', marginLeft: 4 }}>{accrued > 0 ? `${((info.getValue() / accrued) * 100).toFixed(0)}%` : ''}</span></span> },
  ];
  const runColumns = [
    { id: 'week', header: 'Week', size: 180, enableSorting: false, meta: { mobile: 'primary' },
      cell: info => { const r = info.row.original; return `${format(new Date(r.week_start + 'T12:00:00'), 'MMM d')}–${format(new Date(r.week_end + 'T12:00:00'), 'MMM d, yyyy')}`; } },
    { accessorKey: 'staff_count', header: 'Staff', size: 80, enableSorting: false, meta: { mobile: 'secondary' }, cell: info => info.getValue() ?? '—' },
    { accessorKey: 'total_hours', header: 'Hours', size: 90, enableSorting: false, meta: { mobile: 'secondary' }, cell: info => info.getValue() != null ? fmtH(info.getValue()) : '—' },
    { accessorKey: 'total_pay', header: 'Total pay', size: 110, enableSorting: false, meta: { mobile: 'primary' }, cell: info => info.getValue() != null ? fmt$(info.getValue()) : '—' },
    { accessorKey: 'status', header: 'Status', size: 120, enableSorting: false, meta: { mobile: 'secondary' },
      cell: info => { const s = info.getValue(); return <span style={{ fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 20, background: s === 'finalized' ? 'var(--success-bg)' : 'var(--warn-bg)', color: s === 'finalized' ? 'var(--success)' : 'var(--warn)' }}>{s === 'finalized' ? '✓ Finalized' : '✎ Draft'}</span>; } },
  ];

  if (loading) return <div className="empty-state"><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>;

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10, marginBottom: 18 }}>
        <NavCard title="Claims"      sub="Runs · filing · receivables"        icon="🧾" to="/finance/claims" />
        <NavCard title="Payroll"     sub="Weekly runs · Sun–Sat"              icon="💸" to="/finance/payroll" />
        <NavCard title="P&L"         sub="Monthly profit & loss · overhead"   icon="📊" to="/finance/pl" />
        <NavCard title="Projections" sub="What-if on authorized hours"        icon="📈" to="/finance/pl?tab=projections" />
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 500 }}>Period:</span>
        {PERIODS.map(p => <button key={p.key} onClick={() => setPeriod(p.key)} style={pill(period === p.key)}>{p.label}</button>)}
      </div>

      <Section>Revenue vs payroll — {periodLabel}</Section>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginBottom: 14 }}>
        <KPI label="Revenue (accrued)" value={fmt$(accrued)}   sub={`${fmtH(hoursBilled)} billed · by service date`} />
        <KPI label="Collected"         value={fmt$(collected)} sub="by paid date" />
        <KPI label="Payroll"           value={fmt$(payroll)}   sub={`${fmtH(hoursPaid)} paid · runs by week start`} />
        <KPI label="Gross margin"      value={margin != null ? fmtPct(margin) : '—'} sub={`${fmt$(accrued - payroll)} before overhead`} warn={margin != null && margin < 30} />
      </div>

      <Section>Authorized vs delivered — {periodLabel}</Section>
      <div className="card" style={{ marginBottom: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 0 }}>
          {[
            { label: 'Authorized', value: fmt$(authorized), sub: `${fmtH(authorizedHours)} · ${authClients.length} in service · ${weeksInPeriod.toFixed(1)} wks${pEnd > now ? ' to date' : ''}` },
            { label: 'Delivered (billed)', value: fmt$(accrued), sub: `${fmtH(hoursBilled)} on claim lines` },
            { label: 'Utilisation', value: utilisation != null ? fmtPct(utilisation) : '—', sub: 'of authorized hours delivered', warn: utilisation != null && utilisation < 70 },
            { label: 'Unused authorization', value: fmt$(Math.max(0, authorized - accrued)), sub: 'revenue the auths allow that was not billed' },
          ].map((k, i) => (
            <div key={k.label} style={{ padding: '14px 18px', borderRight: i < 3 ? '0.5px solid var(--border)' : 'none' }}>
              <div style={{ fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text3)', marginBottom: 6 }}>{k.label}</div>
              <div style={{ fontSize: 20, fontWeight: 500, color: k.warn ? 'var(--danger)' : 'var(--text)' }}>{k.value}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>{k.sub}</div>
            </div>
          ))}
        </div>
        <div style={{ padding: '8px 18px', borderTop: '0.5px solid var(--border)', fontSize: 11, color: 'var(--text3)' }}>
          Prior-auth hours × fee-schedule rate for everyone in service. The what-if version with editable rates and overhead is under{' '}
          <span style={{ cursor: 'pointer', color: 'var(--accent2)', marginLeft: 4 }} onClick={() => navigate('/finance/pl?tab=projections')}>P&amp;L › Projections →</span>
        </div>
      </div>

      <Section>Claims</Section>
      <div className="card" style={{ marginBottom: 14, padding: '12px 18px', display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center', fontSize: 12.5 }}>
        <span><b style={{ fontSize: 16 }}>{fmt$(outstanding.reduce((s, c) => s + (c.amount || 0), 0))}</b> <span style={{ color: 'var(--text3)' }}>outstanding · {outstanding.length} filed, open</span></span>
        <span><b style={{ fontSize: 16 }}>{fmt$(unfiled.reduce((s, c) => s + (c.amount || 0), 0))}</b> <span style={{ color: 'var(--text3)' }}>not yet filed · {unfiled.length} lines</span></span>
        <span><b style={{ fontSize: 16, color: aging.d > 0 ? 'var(--danger)' : 'var(--text)' }}>{fmt$(aging.d)}</b> <span style={{ color: 'var(--text3)' }}>over 90 days · {aging.dCt} claims</span></span>
        <span style={{ marginLeft: 'auto', cursor: 'pointer', color: 'var(--accent2)', fontWeight: 500 }} onClick={() => navigate('/finance/claims?tab=receivables')}>Receivables, aging and denials →</span>
      </div>

      <Section>By client — {periodLabel}</Section>
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-header">
          <div className="card-title">Revenue and payroll by client</div>
          <span style={{ fontSize: 11, color: 'var(--text3)' }}>{byClient.length} clients · revenue from claim lines, payroll from run lines</span>
        </div>
        <DataTable tableId="finance-by-client" columns={clientColumns} data={byClient} getRowId={r => r.key}
          showColumnPicker={false} maxHeight="420px" emptyMessage="No billed or paid hours in this period." />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14, marginBottom: 14 }}>
        <div className="card">
          <div className="card-header"><div className="card-title">Service mix</div></div>
          <DataTable tableId="finance-by-service" columns={mixColumns('Service')} data={byService} getRowId={r => r.key} showColumnPicker={false} maxHeight="300px" emptyMessage="—" />
        </div>
        <div className="card">
          <div className="card-header"><div className="card-title">By payer</div></div>
          <DataTable tableId="finance-by-payer" columns={mixColumns('Payer')} data={byPayer} getRowId={r => r.key} showColumnPicker={false} maxHeight="300px" emptyMessage="—" />
        </div>
      </div>

      <Section>Payroll</Section>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginBottom: 14 }}>
        <KPI label="Last run payout" value={lastFinalized ? fmt$(lastFinalized.total_pay) : '—'} sub={lastFinalized ? `${format(new Date(lastFinalized.week_start + 'T12:00:00'), 'MMM d')}–${format(new Date(lastFinalized.week_end + 'T12:00:00'), 'MMM d')}` : 'No finalized runs'} />
        <KPI label="YTD payroll"     value={fmt$(payrollYTD)} sub={`${now.getFullYear()} finalized runs`} />
        <KPI label="Draft runs"      value={drafts.length} sub={drafts.length ? 'awaiting finalization' : 'all finalized'} warn={drafts.length > 1} />
        <KPI label="Last run staff"  value={lastFinalized?.staff_count ?? '—'} sub={lastFinalized ? `${fmtH(lastFinalized.total_hours)} total` : '—'} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14 }}>
        <div className="card">
          <div className="card-header">
            <div className="card-title">Pay by caregiver — {periodLabel}</div>
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>{byCaregiver.length} people</span>
          </div>
          <DataTable tableId="finance-by-caregiver" columns={caregiverColumns} data={byCaregiver} getRowId={r => r.key} showColumnPicker={false} maxHeight="360px" emptyMessage="No payroll lines in this period." />
        </div>
        <div className="card">
          <div className="card-header">
            <div className="card-title">Recent payroll runs</div>
            <button onClick={() => navigate('/finance/payroll')} style={{ fontSize: 12, color: 'var(--accent2)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>View all →</button>
          </div>
          <DataTable tableId="finance-overview-runs" columns={runColumns} data={runs.slice(0, 6)} getRowId={r => r.id}
            onRowClick={r => navigate(`/finance/payroll/${r.id}`)} showColumnPicker={false} maxHeight="360px" emptyMessage="No payroll runs yet." />
        </div>
      </div>
    </div>
  );
}
